import React from "react";
import { ReduxState, ReduxDispatch } from "../../ducks/reducers";
import {
    SetShowBackNavigation,
    SetHeader,
    SetBottomBarTab,
} from "../../ducks/common";
import { connect } from "react-redux";
import { PortfolioDto } from "../../util/DTOs/PortfolioDtos";
import { AccountSummaryDto } from "../../util/DTOs/AccountDtos";
import { LoanSummaryDto } from "../../util/DTOs/LoanDtos";
import { CreditCardSummaryDto } from "../../util/DTOs/CreditCardDtos";
import { Grid } from "material-ui";
import {
    getPortfolio,
    getPortfolioData,
    KycDialogContent,
    KycDialogState,
    setKycDialogContent,
    setKycDialogState,
} from "../../ducks/portfolio";
import CreditCardIcon from "material-ui-icons/CreditCard";
import EuroIcon from "material-ui-icons/EuroSymbol";
import LoanIcon from "material-ui-icons/AccountBalance";
import { formatNumber, lowToHigh, highToLow } from "../../util/utils";
import { RouteProps, RouterProps } from "react-router";
import FullPageSpinner from "../../components/FullPageSpinner/FullPageSpinner";
import { PortfolioTab } from "components/BottomBar/BottomBar";
import { getMessageThreads, getThreads } from "../../ducks/messages";
import { getAmlClass, getAmlStatus } from "../../ducks/profile";
import {
    AMLRiskAssesmentClassDto,
    AmlRiskAssesmentStatus,
    AmlRiskAssesmentStatusType,
} from "../../util/DTOs/ProfileDtos";
import KycDialog, { DialogType } from "../../components/Dialog/KycDialog";
import { KYCLatestAnswerDateDto } from "util/DTOs/KYCDtos";
import { getInterestedPartyKycLatestAnswerDate } from "ducks/kyc";
import moment from "moment";

const assetPalette = [
    "#C8E6C9",
    "#A5D6A7",
    "#81C784",
    "#66BB6A",
    "#4CAF50",
    "#43A047",
    "#388E3C",
    "#2E7D32",
    "#1B5E20",
];

const debtPalette = [
    "#BBDEFB",
    "#90CAF9",
    "#64B5F6",
    "#42A5F5",
    "#2196F3",
    "#1E88E5",
    "#1976D2",
    "#1565C0",
    "#0D47A1",
];

const AppBarHeight = 50;
const BottomBarHeight = 56;
const MinHeight = 48;
const MinBarHeight = 5;

const sortAccounts = (a: AccountSummaryDto, b: AccountSummaryDto) => {
    return lowToHigh(a.balance, b.balance);
};

const sortLoans = (a: LoanSummaryDto, b: LoanSummaryDto) => {
    return highToLow(a.currentBalance, b.currentBalance);
};

const sortCreditCards = (a: CreditCardSummaryDto, b: CreditCardSummaryDto) => {
    return highToLow(a.balance, b.balance);
};

interface IPortfolioPageProps {}

interface IPortfolioPageReduxProps {
    portfolio: PortfolioDto;
    loading: boolean;
    threadsInState: boolean;
    aml?: AMLRiskAssesmentClassDto;
    kycDialogState: KycDialogState;
}

interface IPortfolioPageDispatchProps {
    showBackButton: (show: boolean) => void;
    setHeader: (header: string) => void;
    getPortfolioData: () => void;
    setBottomBarTab: () => void;
    fetchMessageThreads: () => void;
    getAmlRisk: () => Promise<AMLRiskAssesmentClassDto>;
    getAmlRiskStatus: () => Promise<AmlRiskAssesmentStatus>;
    getLatestKycAnswerDate: () => Promise<KYCLatestAnswerDateDto>;
    setDialogState: (state: boolean) => void;
    setDialogContent: (content: KycDialogContent) => void;
}

interface DialogRect {
    top: number;
    left: number;
    height: number;
    width: number;
}

interface DialogBaseContent {
    name: string;
    amount: number;
}

interface IPortfolioPageState {
    windowWidth: number;
    windowHeight: number;
    id?: string | number;
}

type PortfolioPageProps = IPortfolioPageProps &
    IPortfolioPageDispatchProps &
    IPortfolioPageReduxProps &
    RouteProps &
    RouterProps;

class PortfolioPage extends React.Component<
    PortfolioPageProps,
    IPortfolioPageState
> {
    constructor(props: PortfolioPageProps) {
        super(props);

        this.state = {
            windowHeight: window.innerHeight,
            windowWidth: window.innerWidth,
        };
    }

    windowResize = () => {
        this.setState((prevState) => ({
            ...prevState,
            windowWidth: this.calculateWindowWidth(),
            windowHeight: this.calculateWindowHeight(),
        }));
    };

    calculateWindowWidth() {
        return window.innerWidth;
    }

    calculateWindowHeight() {
        return window.innerHeight;
    }

    componentDidMount() {
        this.props.getPortfolioData();
        this.props.showBackButton(false);
        this.props.setHeader("Varallisuus");
        this.props.setBottomBarTab();
        this.props.getAmlRiskStatus().then((result) => {
            const title = "Tarkista tietosi";
            const content = [
                `Ylläpidämme asiakkaidemme tuntemistietoja säännöllisesti osana lakisääteisiä velvoitteitamme.`,
                `Pyydämme sinua tarkastamaan, ovatko tietosi muuttuneet.`,
            ];
            switch (result.amlRiskAssesmentStatusType) {
                case AmlRiskAssesmentStatusType.Required:
                    this.props.setDialogContent({
                        title: title,
                        content: content,
                        type: DialogType.Required,
                    });
                    // Only initialize dialog state if it has not been already (== undefined)
                    if (
                        this.props.kycDialogState.open === undefined ||
                        !this.props.kycDialogState.open
                    ) {
                        // 2. Set dialog state according to AML Class & last revision date
                        this.props.setDialogState(true);
                    }
                    break;
                case AmlRiskAssesmentStatusType.Optional:
                    this.props.setDialogContent({
                        title: title,
                        content: content,
                        type: DialogType.Optional,
                    });
                    // Only initialize dialog state if it has not been already (== undefined)
                    if (this.props.kycDialogState.open === undefined) {
                        // 2. Set dialog state according to AML Class & last revision date
                        this.props.setDialogState(true);
                    }
                    break;
                default:
                    break;
            }
        });

        if (!this.props.threadsInState) {
            this.props.fetchMessageThreads();
        }

        window.scrollTo(0, 0);

        window.addEventListener("resize", this.windowResize);
    }

    componentWillUnmount() {
        this.props.setHeader("");
        window.removeEventListener("resize", this.windowResize);
    }

    openAccountPage = (id: number) => {
        this.props.history.push(`/account/${id}`);
    };

    openLoanPage = (id: number) => {
        this.props.history.push(`/loan/${id}`);
    };

    openCreditCardPage = (creditCardNumber: string) => {
        this.props.history.push(`/creditcards/${creditCardNumber}`);
    };

    getKycDialogStatus = (): boolean => {
        return this.props.kycDialogState.open === true;
    };

    closeDialog = () => {
        this.props.setDialogState(false);
    };

    openProfilePage = () => {
        this.props.setDialogState(false);
        this.props.history.push("/profile?showKyc=true");
    };

    renderAssets = (accounts: AccountSummaryDto[]) => {
        let items: JSX.Element[] = [];
        let idx = 0;
        accounts.forEach((acc) => {
            const height = MinHeight;
            const barHeight = MinHeight;
            const barWidth = barHeight;
            const topMargin = 0;

            let item = (
                <Grid
                    tabIndex={0}
                    key={"account" + idx}
                    style={{
                        minHeight: height,
                        cursor: "pointer",
                        marginBottom: 8,
                    }}
                    item
                    xs={12}
                    onClick={() => {
                        this.openAccountPage(acc.id);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            this.openAccountPage(acc.id);
                        }
                    }}
                    className={"gridItem"}
                >
                    <div
                        className="funds-icon-container"
                        style={{
                            minHeight: barHeight,
                            backgroundColor: "#63A51D",
                            width: barWidth,
                        }}
                    >
                        <EuroIcon
                            style={{
                                width: 36,
                                height: 36,
                                fill: "#FFF",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            float: "left",
                            paddingLeft: 8,
                            width: "calc(100% - 48px - 8px)",
                        }}
                    >
                        <div style={{ width: "100%" }}>
                            <div
                                style={{ float: "right" }}
                                className="funds-title"
                            >
                                {formatNumber(acc.balance)} €
                            </div>
                            <div
                                className="funds-title"
                                style={{ overflow: "hidden", width: "auto" }}
                            >
                                {acc.accountName || "Talletustili"}
                            </div>
                        </div>
                        <div className="funds-value" style={{ width: "100%" }}>
                            {acc.accountNumber}
                        </div>
                    </div>
                </Grid>
            );
            items.push(item);
            ++idx;
        });

        return items;
    };

    renderDebts = (
        loans: LoanSummaryDto[],
        creditCards: CreditCardSummaryDto[]
    ) => {
        let items: JSX.Element[] = [];
        let idx = 0;
        loans.forEach((loan) => {
            const height = MinHeight;
            const barHeight = MinHeight;
            const barWidth = barHeight;
            const topMargin = 0;

            let item = (
                <Grid
                    tabIndex={0}
                    key={"loan" + idx}
                    item
                    xs={12}
                    onClick={() => {
                        this.openLoanPage(loan.id);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            this.openLoanPage(loan.id);
                        }
                    }}
                    style={{
                        minHeight: height,
                        cursor: "pointer",
                        marginTop: 0,
                        marginBottom: 8,
                    }}
                    className={"gridItem"}
                >
                    <div
                        className="funds-icon-container"
                        style={{
                            minHeight: barHeight,
                            backgroundColor: "#2196F2",
                            width: barWidth,
                        }}
                    >
                        <LoanIcon
                            style={{
                                width: 36,
                                height: 36,
                                fill: "#FFF",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            float: "left",
                            paddingLeft: 8,
                            width: "calc(100% - 48px - 8px)",
                            marginTop: 0,
                        }}
                    >
                        <div style={{ width: "100%" }}>
                            <div
                                style={{ float: "right" }}
                                className="funds-title"
                            >
                                {formatNumber(loan.currentBalance)} €
                            </div>
                            <div
                                className="funds-title"
                                style={{ width: "auto", overflow: "hidden" }}
                            >
                                {loan.loanName || loan.loanTypeName}
                            </div>
                        </div>
                        <div className="funds-value" style={{ width: "100%" }}>
                            {loan.loanNumber}
                        </div>
                    </div>
                </Grid>
            );
            items.push(item);
            ++idx;
        });

        creditCards.forEach((card) => {
            const height = MinHeight;
            const barHeight = MinHeight;
            const barWidth = barHeight;
            const topMargin = 0;

            let item = (
                <Grid
                    tabIndex={0}
                    key={"card" + idx}
                    item
                    xs={12}
                    onClick={() => {
                        this.openCreditCardPage(card.creditCardNumber);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            this.openCreditCardPage(card.creditCardNumber);
                        }
                    }}
                    style={{
                        minHeight: height,
                        cursor: "pointer",
                        position: "relative",
                        marginTop: 0,
                        marginBottom: 8,
                    }}
                    className={"gridItem"}
                >
                    <div
                        className="funds-icon-container"
                        style={{
                            minHeight: barHeight,
                            backgroundColor: `${
                                debtPalette[idx % (debtPalette.length - 1)]
                            }`,
                            width: barWidth,
                        }}
                    >
                        <CreditCardIcon
                            style={{
                                width: 36,
                                height: 36,
                                fill: "#FFF",
                            }}
                        />
                    </div>
                    <div
                        style={{
                            float: "left",
                            paddingLeft: 8,
                            width: "calc(100% - 48px - 8px)",
                            marginTop: 0,
                        }}
                    >
                        <div style={{ width: "100%" }}>
                            <div
                                style={{ float: "right" }}
                                className="funds-title"
                            >
                                {formatNumber(card.balance)} €
                            </div>
                            <div
                                className="funds-title"
                                style={{ widht: "auto", overflow: "hidden" }}
                            >
                                {card.creditCardName}
                            </div>
                        </div>
                        <div className="funds-value" style={{ width: "100%" }}>
                            {card.creditCardName}
                        </div>
                    </div>
                </Grid>
            );
            items.push(item);
            ++idx;
        });

        return items;
    };

    render() {
        const { accounts, loans, creditCards } = this.props.portfolio;

        const accountsValue = accounts.reduce(
            (val, acc) => val + acc.balance,
            0
        );

        const loansValue = loans.reduce(
            (val, loan) => val + loan.currentBalance,
            0
        );
        const creditCardsValue = creditCards.reduce(
            (val, cc) => val + Math.abs(cc.balance || 0),
            0
        );
        const debtsValue = loansValue + creditCardsValue;

        const assetsStr = formatNumber(accountsValue);
        const debtsStr = formatNumber(debtsValue);

        return (
            <div>
                <Grid
                    container
                    spacing={0}
                    style={{
                        position: "relative",
                        paddingLeft: 8,
                        marginTop: 16,
                    }}
                >
                    {this.renderAssets(accounts.sort(sortAccounts))}
                    <Grid item xs={12} style={{ height: 20, marginTop: 8 }}>
                        <div
                            style={{
                                position: "absolute",
                                left: 5,
                                bottom: 2,
                                backgroundColor: "white",
                            }}
                        >
                            <h4 className="no-margin hypogreen light">
                                Talletukset yhteensä
                            </h4>
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                right: 5,
                                bottom: 2,
                                backgroundColor: "white",
                            }}
                        >
                            <h4 className="no-margin hypogreen light">{`=${assetsStr} €`}</h4>
                        </div>
                    </Grid>
                </Grid>
                <Grid container spacing={0}>
                    <hr className="funds-hr" />
                </Grid>
                <Grid
                    container
                    spacing={0}
                    style={{ position: "relative", paddingLeft: 8 }}
                >
                    <Grid item xs={12} style={{ height: 20, marginBottom: 16 }}>
                        <div
                            style={{
                                position: "absolute",
                                left: 5,
                                top: 0,
                                backgroundColor: "white",
                            }}
                        >
                            <h4 className="no-margin hypolblue light">
                                Velat yhteensä
                            </h4>
                        </div>
                        <div
                            style={{
                                position: "absolute",
                                right: 5,
                                top: 0,
                                backgroundColor: "white",
                            }}
                        >
                            <h4 className="no-margin hypolblue light">{`=${debtsStr} €`}</h4>
                        </div>
                    </Grid>
                    {this.renderDebts(
                        loans.sort(sortLoans),
                        creditCards.sort(sortCreditCards)
                    )}
                </Grid>
                <FullPageSpinner loading={this.props.loading} />
                {this.props.kycDialogState && (
                    <KycDialog
                        open={this.getKycDialogStatus()}
                        title={this.props.kycDialogState.title}
                        content={this.props.kycDialogState.content}
                        type={this.props.kycDialogState.type}
                        onConfirm={this.openProfilePage}
                        onCancel={this.closeDialog}
                    ></KycDialog>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: ReduxState): IPortfolioPageReduxProps => {
    const realPortfolio = getPortfolioData(state);
    const threads = getThreads(state);
    const hasThreads = Object.keys(threads).length > 0;

    const loadingData =
        realPortfolio.loading &&
        realPortfolio.accounts.length === 0 &&
        realPortfolio.loans.length === 0 &&
        realPortfolio.creditCards.length === 0;

    return {
        portfolio: {
            accounts: [...realPortfolio.accounts],
            loans: [...realPortfolio.loans],
            creditCards: [...realPortfolio.creditCards],
        },
        loading: loadingData,
        threadsInState: hasThreads,
        aml: state.profile.aml,
        kycDialogState: state.portfolio.kycDialogState,
    };
};

const mapDispatchToProps = (
    dispatch: ReduxDispatch
): IPortfolioPageDispatchProps => {
    return {
        showBackButton: (show: boolean) => {
            dispatch(SetShowBackNavigation(show));
        },
        setHeader: (header: string) => {
            dispatch(SetHeader(header));
        },
        getPortfolioData: async () => {
            await dispatch(getPortfolio());
        },
        setBottomBarTab: () => {
            dispatch(SetBottomBarTab(PortfolioTab));
        },
        fetchMessageThreads: () => {
            dispatch(getMessageThreads());
        },
        getAmlRisk: () => dispatch(getAmlClass()),
        setDialogState: (state: boolean) => {
            dispatch(setKycDialogState(state));
        },
        getAmlRiskStatus: () => dispatch(getAmlStatus()),
        setDialogContent: (content: KycDialogContent) => {
            dispatch(setKycDialogContent(content));
        },
        getLatestKycAnswerDate: () =>
            dispatch(getInterestedPartyKycLatestAnswerDate()),
    };
};

export default connect<
    IPortfolioPageReduxProps,
    IPortfolioPageDispatchProps,
    IPortfolioPageProps
>(
    mapStateToProps,
    mapDispatchToProps
)(PortfolioPage);



// WEBPACK FOOTER //
// ./src/views/portfolio/PortfolioPage.tsx